import React, { useEffect, useRef } from 'react';
import { MarkdownHooks } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeMermaid from 'rehype-mermaid';
import { FaThumbsUp, FaThumbsDown } from 'react-icons/fa';

const ChatHistory = ({ messages, isThinking, chatContainerRef, onFeedback }) => {
  const innerRef = useRef();

  useEffect(() => {
    if (innerRef.current) {
      const length = innerRef.current.childNodes.length;
      if (length > 0) innerRef.current.childNodes[length - 1].scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
    }
  }, [messages.length]);

  return (
    <div className="flex-grow-1 mb-3" style={{ overflowY: "auto" }}>
      <div
        ref={chatContainerRef}
        className="d-flex justify-content-center flex-grow-1 mb-3"
        style={{ overflowX: "auto", overflowY: "auto", paddingBottom: "11rem" }}
      >
        <div ref={innerRef} style={{ width: "100%" }}>
          {messages.map((msg, idx) => {
            if (!msg.content?.[0]?.text) return null;

            return (
              <div
                key={idx}
                className={`d-flex mb-2 ${
                  msg.role === "user"
                    ? "justify-content-end"
                    : "justify-content-start"
                }`}
              >
                <div
                  className={`px-3 py-2 rounded-3 shadow-sm ${
                    msg.role === "user"
                      ? "bg-primary text-white"
                      : "bg-light text-dark"
                  }`}
                  style={{
                    ...(msg.role === "user"
                      ? { maxWidth: "75%" }
                      : { width: "75%" }),
                    wordWrap: "break-word",
                    borderRadius: "16px",
                  }}
                >
                  {msg.role === "assistant" ? (
                    <>
                      <MarkdownHooks
                        children={msg.content[0].text}
                        rehypePlugins={[
                          [
                            rehypeMermaid,
                            {
                              errorFallback: (element, diagram, error) => {
                                console.warn(
                                  "[Mermaid] error fallback:",
                                  error.message
                                );
                                return {
                                  type: "element",
                                  tagName: "pre",
                                  properties: {},
                                  children: [{ type: "text", value: diagram }],
                                };
                              },
                            },
                          ],
                        ]}
                        remarkPlugins={[remarkGfm, remarkBreaks]}
                        components={{
                          h1: "h3",
                          h2: "h4",
                          h3: "h4",
                          a: (props) => (
                            <a
                              {...props}
                              target="_blank"
                              rel="noopener noreferrer"
                            />
                          ),
                          code: ({ inline, children }) =>
                            !inline ? (
                              <pre
                                style={{
                                  background: "#f6f8fa",
                                  padding: "0.75rem",
                                  borderRadius: "6px",
                                }}
                              >
                                <code>{children}</code>
                              </pre>
                            ) : (
                              <code
                                style={{
                                  background: "#eee",
                                  padding: "0.2rem 0.4rem",
                                  borderRadius: "4px",
                                }}
                              >
                                {children}
                              </code>
                            ),
                        }}
                      />

                      {/* Feedback Buttons - Removed border-top and border-secondary-subtle */}
                      <div className="d-flex gap-2 mt-2 justify-content-end align-items-center">
                        <button
                          onClick={() =>
                            onFeedback && onFeedback(idx, "positive")
                          }
                          className="btn btn-sm border-0 p-0"
                          title="Helpful"
                          style={{
                            color:
                              msg.feedback === "positive"
                                ? "#198754"
                                : "#6c757d",
                            transition: "color 0.2s",
                          }}
                        >
                          <FaThumbsUp size={17} />
                        </button>
                        <button
                          onClick={() =>
                            onFeedback && onFeedback(idx, "negative")
                          }
                          className="btn btn-sm border-0 p-0"
                          title="Not Helpful"
                          style={{
                            color:
                              msg.feedback === "negative"
                                ? "#dc3545"
                                : "#6c757d",
                            transition: "color 0.2s",
                          }}
                        >
                          <FaThumbsDown size={17} />
                        </button>
                      </div>
                    </>
                  ) : (
                    msg.content[0].text
                  )}
                </div>
              </div>
            );
          })}

          {isThinking && (
            <div className="d-flex mb-2 justify-content-start">
              <div
                className="px-4 py-3 rounded-3 shadow-sm bg-light"
                style={{
                  width: "fit-content",
                  borderRadius: "20px",
                  borderBottomLeftRadius: "4px",
                }}
              >
                <div className="typing-indicator">
                  <div className="typing-dot"></div>
                  <div className="typing-dot"></div>
                  <div className="typing-dot"></div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatHistory;